import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock, Eye, EyeSlash, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChangePasswordCard = () => {
  const { user, updateUser } = useAuth();
  const [open, setOpen] = useState(!!user?.must_change_password);
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState({ cur: false, nw: false, cf: false });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.current_password) { toast.error('Enter your current password'); return; }
    if (form.new_password.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (form.new_password !== form.confirm_password) { toast.error('New passwords don\u2019t match'); return; }
    if (form.new_password === form.current_password) { toast.error('New password must be different from current'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success('Password updated successfully');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      setOpen(false);
      // Clear the must_change_password flag locally
      if (user?.must_change_password) updateUser?.({ ...user, must_change_password: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  return (
    <div className={`bg-white rounded-2xl border ${user?.must_change_password ? 'border-amber-300 ring-2 ring-amber-200/60' : 'border-slate-100'} overflow-hidden`} data-testid="change-password-card">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-slate-700"
        data-testid="change-password-toggle"
      >
        <div className="flex items-center gap-3">
          <Lock size={20} />
          <span className="font-medium">Change Password</span>
          {user?.must_change_password && (
            <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-300 px-2 py-0.5 rounded-full font-bold">
              Action required
            </span>
          )}
        </div>
        <span className="text-slate-400 text-sm">{open ? 'Close' : 'Open'}</span>
      </button>

      {open && (
        <form onSubmit={submit} className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-4" data-testid="change-password-form">
          {user?.must_change_password && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
              <strong>Admin reset your password.</strong> Please set a new one to continue using your account safely.
            </div>
          )}

          <PasswordField label="Current password" value={form.current_password}
            onChange={(v) => setForm(f => ({ ...f, current_password: v }))}
            show={show.cur} onToggleShow={() => setShow(s => ({ ...s, cur: !s.cur }))}
            testid="current-password-input" />

          <PasswordField label="New password (min 6 chars)" value={form.new_password}
            onChange={(v) => setForm(f => ({ ...f, new_password: v }))}
            show={show.nw} onToggleShow={() => setShow(s => ({ ...s, nw: !s.nw }))}
            testid="new-password-input" />

          <PasswordField label="Confirm new password" value={form.confirm_password}
            onChange={(v) => setForm(f => ({ ...f, confirm_password: v }))}
            show={show.cf} onToggleShow={() => setShow(s => ({ ...s, cf: !s.cf }))}
            testid="confirm-password-input" />

          <Button type="submit" disabled={busy}
            className="w-full h-11 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 rounded-xl"
            data-testid="change-password-submit">
            <ShieldCheck size={16} weight="fill" className="mr-1.5" />
            {busy ? 'Updating\u2026' : 'Update Password'}
          </Button>
        </form>
      )}
    </div>
  );
};

const PasswordField = ({ label, value, onChange, show, onToggleShow, testid }) => (
  <div>
    <Label className="text-xs text-slate-600">{label}</Label>
    <div className="relative mt-1">
      <Input type={show ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)}
        className="h-11 rounded-xl pr-10" data-testid={testid} autoComplete="new-password" />
      <button type="button" onClick={onToggleShow}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
        tabIndex={-1}>
        {show ? <EyeSlash size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </div>
);

export default ChangePasswordCard;
